import { Component, inject, input, output, TemplateRef, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogClose } from '@angular/material/dialog';
import { MatError, MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { SearchbarComponent } from '../../../common/components/searchbar/searchbar.component';
import { CreateRoomRequest } from '../../../../shared-api/createRoomRequest';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { MXID_PATTERN } from 'matrix-js-sdk';
import { RoomService } from '../../../services/room/room.service';
import { DropdownComponent } from '../../../common/components/dropdown/dropdown.component';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { trimmedMinLengthValidator } from '../../../validators/trimmedMinLength';
import { BaseInvite } from '../../../common/components/base-invite/base-invite';
import { CreateRoomDialogTestIds } from './sb-create-room-dialog.test-ids';
import { AppConfigService } from '../../../services/config/app-config.service';
import { MatSlideToggle } from '@angular/material/slide-toggle';

type DialogContext = object;

@Component({
  selector: 'app-create-room-dialog',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatInput,
    MatDialogClose,
    MatIcon,
    MatTooltip,
    MatSuffix,
    MatTableModule,
    MatError,
    CdkTextareaAutosize,
    ButtonComponent,
    MatOption,
    MatSelect,
    BaseInvite,
    MatSlideToggle
  ],
  templateUrl: './sb-create-room-dialog.component.html',
  styleUrl: './sb-create-room-dialog.component.scss'
})
/**
 * Dialog-Komponente zum Erstellen von Räumen.
 * Folgende Optionen stehen dabei zur Auswahl:
 * * Name des Chats
 * * (einzelne) Einzulandende Matrix-ID
 * * Öffentlich-/Privat-Flag
 *
 * Hierdurch erfüllte AFO`s
 *
 *  A_25813-01 - Pflichtparameter bei der Chatroom-Erzeugung für föderierte und intersektorale Kommunikation
 *  A_25426 - Verwendung des Chatroom-Typen für föderierte und intersektorale Kommunikation
 *  A_25046 - Durchsetzung der Berechtigungskonfiguration - Client
 *  A_25816 - Der TI-M Client MUSS das custom state event aus der AFO erzeugen können.
 *  A_26338-02 - In Räumen mit Custom Room Types die der AFO entnommen werden können, MUSS der Client Custom State
 *  A_25397 - Der TI-M Client MUSS sicherstellen, dass im createRoom-Event maximal die Einladung einer MXID enthalten ist. -->
 *  Events, die der AFO entnommen werden können, zuzüglich zu den standard events verwenden.
 *  A_25325-01 - Öffentliche Räume im Client
 *  A_25573: TLS über HTTPS (Browser/Runtime)
 */
export class SbCreateRoomDialogComponent {
  readonly dialog = viewChild.required<TemplateRef<DialogContext>>('inviteDialog');
  readonly searchbar = viewChild.required<SearchbarComponent>('searchbar');
  readonly matDialog = inject(MatDialog);
  navigatedRoom = output<string>();
  collapsed = input<boolean>();
  readonly publicDropdownComponent = input<DropdownComponent>();
  readonly privateDropdownComponent = input<DropdownComponent>();
  isCreatingRoom = false;
  showSpinner = false;
  protected readonly testIds = CreateRoomDialogTestIds;
  private readonly router = inject(Router);
  private readonly snackbarService = inject(SnackbarService);
  private readonly fb = inject(FormBuilder);
  /**
   * FormGroup für das Formular zum Erstellen eines Chats.
   */
  public readonly createForm = this.fb.group({
    alias: ['', [Validators.minLength(2), Validators.pattern(/^[A-Za-z0-9äöüÄÖÜß]+$/)]],
    name: ['', [Validators.required, trimmedMinLengthValidator(2), Validators.pattern(/^[A-Za-z0-9äöüÄÖÜß\s]*$/)]],
    visibility: [false],
    topic: [''],
    roomType: [false],
    invites: this.fb.group({
      mxid: ['', Validators.pattern(MXID_PATTERN)]
    })
  });
  private readonly appConfig = inject(AppConfigService);
  private readonly roomService = inject(RoomService);

  get isV2RoomEnabled(): boolean {
    return this.appConfig.get('enableV2Room') === 'true';
  }

  resetForm(): void {
    this.createForm.reset({ topic: '', invites: { mxid: '' }, visibility: false, roomType: false });
    this.matDialog.closeAll();
  }

  openDialog(template: TemplateRef<MatDialog>, $event?: Event): void {
    this.matDialog.closeAll();

    // Problem: Wird Dialog (in E2E-Test) erneut geöffnet, kann es sein, dass vorübergehend zwei Instanzen im DOM sind.
    // Das mag Playwright nicht.
    // Option 1: Dialoge serialisieren
    // Sicherstellen, dass nie zwei Dialoge gleichzeitig offen sind:
    // ✔️ reduziert Overlaps massiv
    // ✔️ kein Konfig-Hack
    // ✔️ production-safe
    if (this.matDialog.openDialogs.length) {
      this.matDialog.closeAll();
    }
    this.matDialog.open(template, {
      width: '53rem',
      autoFocus: true,
      restoreFocus: true
      // Option 2: Animationsdauer für Dialoge auf 0 setzen
      // - Dialog wird praktisch sofort entfernt
      // - In Tests nur noch eine Instanz
      // (Funktioniert auch, aber dann keine Animation)
      // enterAnimationDuration: '0ms',
      // exitAnimationDuration: '0ms'
    });
    if ($event) $event.stopPropagation();
  }

  /**
   * Erzeugt einen neuen Chat.
   */
  protected async createRoom(matrixId: string | undefined | null): Promise<void> {
    this.showSpinner = true;
    if (!this.isCreatingRoom && this.createForm.valid) {
      this.isCreatingRoom = true;
      const mxid = matrixId || this.createForm.controls.invites.value.mxid;
      const isVisible = this.createForm.value.visibility;

      const createRoomRequest: CreateRoomRequest = {
        roomAccess: this.createForm.value.visibility ? 'public' : 'private',
        name: this.createForm.value.name ?? undefined,
        theme: this.createForm.value.topic ?? undefined,
        alias: this.createForm.value.alias ?? undefined,
        isV2Room: this.createForm.value.roomType === true
      };
      if (mxid) {
        const mxidWithoutSpaces = mxid.split(/\s/).join('');
        createRoomRequest.invitedMembers = [{ mxid: mxidWithoutSpaces }];
      }
      try {
        const room = await this.roomService.createRoom(createRoomRequest);

        const navigated = await this.router.navigate(['rooms', room.roomId]);
        if (navigated) {
          this.navigatedRoom.emit(room.roomId);
          if (isVisible) {
            this.privateDropdownComponent()?.closePanel();
            this.publicDropdownComponent()?.openPanel();
          } else {
            this.publicDropdownComponent()?.closePanel();
            this.privateDropdownComponent()?.openPanel();
          }
        }
      } catch (e) {
        console.error('SbCreateRoomDialogComponent: Fehler beim Erstellen des Raums', createRoomRequest, e);
        this.snackbarService.error('Fehler beim Erstellen des Raums');
      } finally {
        this.showSpinner = false;
        this.isCreatingRoom = false;
        this.createForm.reset({
          topic: '',
          invites: { mxid: '' },
          visibility: false
        });
        this.matDialog.closeAll();
        this.isCreatingRoom = false;
      }
    }
  }
}
